import { ReactNode, createContext, useState } from "react";
import { SignInCredentials } from "../api/auth/auth.requests";
import { useNavigate } from "react-router-dom";
import useAuthService from "../api/auth/auth.service";

// Define the context value type
interface AuthContextType {
  accessToken: string | null;
  setAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  login: (signInData: SignInCredentials) => void;
  logout: () => void;
  isLoggedIn: () => boolean;
  persist: boolean;
  setPersist: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType>({
  accessToken: null,
  setAccessToken: () => {},
  login: () => {},
  logout: () => {},
  isLoggedIn: () => false,
  persist: false,
  setPersist: () => {},
});

interface AuthProviderProps {
  children?: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const initialPersistValue = (): boolean => {
    const item = localStorage.getItem("persist");
    return item ? JSON.parse(item) : false;
  };

  const [persist, setPersist] = useState<boolean>(initialPersistValue);

  const { signIn, logout: apiLogout } = useAuthService();

  const navigate = useNavigate();

  const login = async (signInData: SignInCredentials) => {
    try {
      const response = await signIn(signInData);
      setAccessToken(response.data.accessToken);
      navigate("/route-select");
    } catch (error: any) {
      if (!error.response) {
        alert("No server response");
      } else if (error.response.status === 400) {
        alert("Missing username or password");
      } else if (error.response.status === 401) {
        alert("Unauthorized");
      } else {
        alert("Login failed");
      }
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // Logout API call failed (e.g. token already expired).
      // We still proceed with client-side cleanup below.
    } finally {
      setAccessToken(null);
      navigate("/login");
    }
  };

  const isLoggedIn = () => {
    return accessToken !== null;
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        setAccessToken,
        login,
        logout,
        isLoggedIn,
        persist,
        setPersist,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
